import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js"

export class StripeController extends Controller {
  static values = {
    clientSecret: String,
    publishableKey: String,
    options: Object,
    submitSelector: String,
    radioSelector: String,
  }

  static targets = ["paymentElement", "message", "paymentMethodInput"]

  get submitOutletElement() {
    const element = document.querySelector(this.submitSelectorValue)
    console.log("🔍 [Submit Element] Searching for:", this.submitSelectorValue, "Found:", !!element)
    return element
  }

  get radioOutletElement() {
    const element = document.querySelector(this.radioSelectorValue)
    console.log("🔍 [Radio Element] Searching for:", this.radioSelectorValue, "Found:", !!element)
    return element
  }

  async connect() {

    // Make this controller available globally for debugging
    window.debugStripeController = this;

    // Add global 3DS debugging
    window.addEventListener('message', (event) => {
      console.log("🌐 [GLOBAL MESSAGE] Received message:", {
        origin: event.origin,
        data: event.data,
        source: event.source
      });
      
      // Check for 3DS-related messages
      if (event.data && typeof event.data === 'object') {
        if (event.data.type === 'stripe-3ds-challenge') {
          console.log("🔐 [3DS CHALLENGE] 3DS challenge detected!");
        }
        if (event.data.type === 'stripe-3ds-complete') {
          console.log("✅ [3DS COMPLETE] 3DS challenge completed!");
        }
      }
    });

    if (document.readyState === 'complete') {
      console.log("✅ [CONNECT] document.readyState is 'complete' – start Stripe now");
      this.initializeStripe();
    } else {
      console.log("⏱️ [CONNECT] Waiting for DOMContentLoaded …");
      window.addEventListener('DOMContentLoaded', () => {
        console.log("🚀 DOMContentLoaded – start Stripe");
        this.initializeStripe();
      }, { once: true });
    }

    // When customer selects this payment method, mount the Element
    this.radioOutletElement?.addEventListener('change', () => {
      if (this.radioOutletElement.checked && !this.paymentElement) {
        console.log('🔔 [RADIO] Stripe selected – mounting Payment Element');
        this.setupPaymentElement();
      }
    });
  }

  async initializeStripe() {


    if (!this.publishableKeyValue) {
      console.error("❌ [ERROR] Stripe Publishable Key is MISSING!");
      return;
    }
    
    console.log("⚙️ [OPTIONS] Full options object:", JSON.parse(JSON.stringify(this.optionsValue)));
    if (!this.optionsValue || !this.optionsValue.elementsInitialization) {
      console.error("❌ [ERROR] Stripe Options are MISSING or invalid!");
      return;
    }

    console.log("📡 [STRIPE] Loading Stripe.js library...");
    try {
      this.stripe = await loadStripe(this.publishableKeyValue);
      console.log("✅ [STRIPE] Stripe.js loaded successfully:", !!this.stripe);
    } catch (error) {
      console.error("❌ [STRIPE] Failed to load Stripe.js:", error);
      return;
    }
    
    console.log("🏗️ [SETUP] Starting payment element setup...");
    this.setupPaymentElement();
    console.log("🔌 [INIT] Stripe initialization completed");
  }

  disconnect() {
   
    // Cleanup Stripe elements
    if (this.paymentElement) {
      console.log("🧹 [CLEANUP] Unmounting payment element...");
      try {
        this.paymentElement.unmount()
        console.log("✅ [CLEANUP] Payment element unmounted successfully");
      } catch (e) {
        console.log("⚠️ [CLEANUP] Stripe element unmount error:", e)
      }
      this.paymentElement = null
      console.log("🧹 [CLEANUP] Payment element reference cleared");
    } else {
      console.log("ℹ️ [CLEANUP] No payment element to unmount");
    }
    
    if (this.elements) {
      console.log("🧹 [CLEANUP] Clearing elements reference...");
      this.elements = null
    }
    
    console.log("🔌 [DISCONNECT] Disconnection completed");
  }

  // @action
  async handleSubmit(e) {

    // Bail out if not on the payment method form.
    if (e.target !== this.radioOutletElement.form) {
      console.log("⏭️ [SUBMIT] Skipping - not our payment form");
      return;
    }

    console.log("🎯 [SUBMIT] Radio element checked:", this.radioOutletElement?.checked);
    // Bail out if the current payment method is not selected.
    if (!this.radioOutletElement.checked) {
      console.log("⏭️ [SUBMIT] Skipping - payment method not selected");
      return;
    }

    console.log("🛑 [SUBMIT] Preventing default form submission");
    e.preventDefault()

    // Extensive element state checking
    console.log("🔍 [STATE CHECK] Elements object:", !!this.elements);
    console.log("🔍 [STATE CHECK] Payment element:", !!this.paymentElement);
    console.log("🔍 [STATE CHECK] Stripe object:", !!this.stripe);
    console.log("🔍 [STATE CHECK] Payment method input:", !!this.paymentMethodInputTarget);
    console.log("🔍 [STATE CHECK] Message target:", !!this.messageTarget);

    if (!this.elements) {
      console.error("❌ [ERROR] Elements object is missing!");
      this.messageTarget.textContent = "Payment form not initialized. Please refresh the page.";
      return;
    }

    if (!this.paymentElement) {
      console.error("❌ [ERROR] Payment element is missing!");
      this.messageTarget.textContent = "Payment element not ready. Please refresh the page.";
      return;
    }

    this.setLoading(true)

    console.log("📋 [VALIDATION] Calling elements.submit()...");
    try {
      // Trigger form validation and wallet collection
      const { error: submitError } = await this.elements.submit()
      
      console.log("📋 [VALIDATION] Submit result - Error:", !!submitError);
      if (submitError) {
        console.error("❌ [VALIDATION] Submit error details:", submitError);
        this.messageTarget.textContent = submitError.message
        this.setLoading(false)
        return
      }

      console.log("✅ [VALIDATION] Form validation passed");
      console.log("💳 [PAYMENT METHOD] Creating payment method...");
      
      // Create the PaymentMethod using the details collected by the Payment Element
      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        elements: this.elements,
        params: { billing_details: {} },
      })

      console.log("💳 [PAYMENT METHOD] Creation result - Error:", !!error, "Method:", !!paymentMethod);

      if (error) {
        console.error("❌ [PAYMENT METHOD] Creation error:", error);
        console.error("❌ [PAYMENT METHOD] Error type:", error.type);
        console.error("❌ [PAYMENT METHOD] Error message:", error.message);
        
        if (error.type === "card_error" || error.type === "validation_error") {
          this.messageTarget.textContent = error.message
        } else {
          this.messageTarget.textContent = "An unexpected error occurred."
        }
        this.setLoading(false)
        return
      }

      console.log("✅ [PAYMENT METHOD] Created successfully:", paymentMethod.id);
      console.log("🔍 [3DS DEBUG] Payment method details:", {
        id: paymentMethod.id,
        type: paymentMethod.type,
        card: paymentMethod.card ? {
          brand: paymentMethod.card.brand,
          country: paymentMethod.card.country,
          last4: paymentMethod.card.last4
        } : null
      });
      
      console.log("💾 [FORM] Setting payment method ID in hidden input...");
      this.setLoading(false)
      this.paymentMethodInputTarget.value = paymentMethod.id
      
      console.log("📤 [FORM] Submitting form with payment method:", paymentMethod.id);
      console.log("🔍 [3DS FLOW] About to submit form - this will trigger Payment Intent creation on server");
      console.log("🔍 [3DS FLOW] If 3DS is required, the server should return a Payment Intent with status 'requires_action'");
      
      // Create Payment Intent via API before submitting form
        console.log("🔍 [3DS FLOW] Creating Payment Intent via API...");
        console.log("🔍 [3DS FLOW] Frontend amount from options:", this.optionsValue.elementsInitialization.amount);
        console.log("🔍 [3DS FLOW] Frontend currency:", this.optionsValue.elementsInitialization.currency);
        console.log("🔍 [3DS FLOW] Payment method ID:", this.getPaymentMethodId());
        console.log("🔍 [3DS FLOW] Stripe payment method ID:", paymentMethod.id);
        
        try {
          // Get guest token for API authentication
          const guestToken = window.Spree && window.Spree.current_order_token;
          console.log("🔑 [API AUTH] Guest token available:", !!guestToken);
          console.log("🔑 [API AUTH] Guest token value:", guestToken);
          
          const requestBody = {
            payment_method_id: this.getPaymentMethodId(),
            stripe_payment_method_id: paymentMethod.id
          };
          
          console.log("🔍 [3DS FLOW] Request body:", requestBody);
          
          // Add guest token if available
          if (guestToken) {
            requestBody.guest_token = guestToken;
            console.log("🔑 [API AUTH] Added guest token to request");
          } else {
            console.warn("⚠️ [API AUTH] No guest token available");
          }
          
          console.log("🔍 [3DS FLOW] Final request body:", requestBody);
          
          console.log("🔍 [3DS FLOW] Making API request to /solidus_stripe/api/create_payment_intent");
          const response = await fetch('/solidus_stripe/api/create_payment_intent', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            },
            body: JSON.stringify(requestBody)
          });
          
          console.log("🔍 [3DS FLOW] Raw response object:", response);
          console.log("🔍 [3DS FLOW] Response status:", response.status);
          console.log("🔍 [3DS FLOW] Response ok:", response.ok);
          console.log("🔍 [3DS FLOW] Response headers:", Object.fromEntries(response.headers.entries()));
          
          console.log("🔍 [3DS FLOW] Payment Intent API response status:", response.status);
          console.log("🔍 [3DS FLOW] Payment Intent API response headers:", Object.fromEntries(response.headers.entries()));
          
          if (!response.ok) {
            console.error("❌ [3DS FLOW] Payment Intent API error status:", response.status);
            
            // Try to parse error response
            let errorMessage = "Payment processing failed. Please try again.";
            try {
              const errorResult = await response.json();
              console.log("❌ [3DS FLOW] Error response JSON:", errorResult);
              if (errorResult.error) {
                errorMessage = errorResult.error;
              }
            } catch (parseError) {
              console.error("❌ [3DS FLOW] Could not parse error response:", parseError);
              // Try to get the raw response text
              const responseText = await response.text();
              console.log("❌ [3DS FLOW] Raw error response:", responseText);
            }
            
            this.messageTarget.textContent = errorMessage;
            this.setLoading(false);
            return;
          }
          
          console.log("🔍 [3DS FLOW] About to parse response as JSON...");
          const result = await response.json();
          console.log("🔍 [3DS FLOW] Payment Intent creation result:", result);
          console.log("🔍 [3DS FLOW] Result type:", typeof result);
          console.log("🔍 [3DS FLOW] Result keys:", Object.keys(result));
          console.log("🔍 [3DS FLOW] Client secret present:", !!result.client_secret);
          console.log("🔍 [3DS FLOW] Client secret value:", result.client_secret ? result.client_secret.substring(0, 20) + "..." : "null");
          console.log("🔍 [3DS FLOW] Requires action:", !!result.requires_action);
          console.log("🔍 [3DS FLOW] Requires action value:", result.requires_action);
          
          if (result.error) {
            console.error("❌ [3DS FLOW] Payment Intent creation failed:", result.error);
            this.messageTarget.textContent = result.error;
            this.setLoading(false);
            return;
          }
          
          if (result.client_secret) {
            console.log("✅ [3DS FLOW] Payment Intent created with client secret:", result.client_secret.substring(0, 20) + "...");
            
            // Check if 3DS is required
            if (result.requires_action) {
              console.log("🔐 [3DS FLOW] 3DS required! Handling 3DS challenge...");
              await this.handle3DSChallenge(result.client_secret);
            } else {
              console.log("✅ [3DS FLOW] No 3DS required, proceeding with form submission");
              this.paymentMethodInputTarget.form.submit();
            }
          } else {
            console.log("✅ [3DS FLOW] Payment Intent created, proceeding with form submission");
            this.paymentMethodInputTarget.form.submit();
          }
          
        } catch (error) {
          console.error("❌ [3DS FLOW] Payment Intent creation error:", error);
          console.error("❌ [3DS FLOW] Error stack:", error.stack);
          this.messageTarget.textContent = "Payment processing failed. Please try again.";
          this.setLoading(false);
        }
      
    } catch (unexpectedError) {
      console.error("❌ [UNEXPECTED] Error during submit process:", unexpectedError);
      console.error("❌ [UNEXPECTED] Error stack:", unexpectedError.stack);
      this.messageTarget.textContent = "Payment processing failed. Please try again.";
      this.setLoading(false);
    }
  }

  setupPaymentElement() {
    console.log("🏗️ [SETUP] Starting payment element setup...");
    console.log("🏗️ [SETUP] Current payment element exists:", !!this.paymentElement);
    
    // Cleanup any existing elements first
    if (this.paymentElement) {
      console.log("🧹 [SETUP] Cleaning up existing payment element...");
      try {
        this.paymentElement.unmount()
        console.log("✅ [SETUP] Previous element unmounted successfully");
      } catch (e) {
        console.log("⚠️ [SETUP] Previous Stripe element cleanup error:", e)
      }
    }

    console.log("🏗️ [SETUP] Creating new Stripe elements...");
    console.log("🏗️ [SETUP] Elements initialization options:", this.optionsValue.elementsInitialization);
    
    try {
      this.elements = this.stripe.elements(this.optionsValue.elementsInitialization)
      console.log("✅ [SETUP] Stripe elements created:", !!this.elements);
    } catch (error) {
      console.error("❌ [SETUP] Failed to create Stripe elements:", error);
      this.messageTarget.textContent = "Failed to initialize payment form. Please refresh the page.";
      return;
    }

    console.log("🏗️ [SETUP] Creating payment element...");
    console.log("🏗️ [SETUP] Payment element options:", this.optionsValue.paymentElementCreation);
    
    try {
      this.paymentElement = this.elements.create("payment", this.optionsValue.paymentElementCreation)
      console.log("✅ [SETUP] Payment element created:", !!this.paymentElement);
    } catch (error) {
      console.error("❌ [SETUP] Failed to create payment element:", error);
      this.messageTarget.textContent = "Failed to create payment form. Please refresh the page.";
      return;
    }
    
    console.log("🧹 [SETUP] Clearing loading message from target...");
    console.log("🧹 [SETUP] Target element:", this.paymentElementTarget);
    console.log("🧹 [SETUP] Target element dimensions BEFORE clear:", {
      width: this.paymentElementTarget.offsetWidth,
      height: this.paymentElementTarget.offsetHeight,
      display: getComputedStyle(this.paymentElementTarget).display,
      visibility: getComputedStyle(this.paymentElementTarget).visibility,
      position: getComputedStyle(this.paymentElementTarget).position
    });
    
    this.paymentElementTarget.innerHTML = "" // Remove loading message
    
    console.log("📌 [MOUNT] Attempting to mount payment element...");
    console.log("📌 [MOUNT] Target element state before mount:", {
      exists: !!this.paymentElementTarget,
      inDOM: document.contains(this.paymentElementTarget),
      className: this.paymentElementTarget.className,
      id: this.paymentElementTarget.id
    });
    
    // Removed offsetParent visibility guard – we now rely on radio change handler to mount at the correct time.
    
    try {
      this.paymentElement.mount(this.paymentElementTarget)
      console.log("✅ [MOUNT] Stripe payment element mounted successfully");
      
              // Quick post-mount verification
        setTimeout(() => {
          console.log("✅ [POST-MOUNT] Stripe element mounted and ready");
          const dimensions = {
            width: this.paymentElementTarget.offsetWidth,
            height: this.paymentElementTarget.offsetHeight
          };
          console.log("📏 [POST-MOUNT] Element dimensions:", dimensions);
          
          const stripeIframes = this.paymentElementTarget.querySelectorAll('iframe');
          console.log("🖼️ [POST-MOUNT] Stripe iframes found:", stripeIframes.length);
        }, 100);
      
      // Add event listeners for better debugging
      console.log("👂 [EVENTS] Adding event listeners...");
      
      this.paymentElement.on('ready', () => {
        console.log("🎉 [READY] Payment element is ready for user interaction");
        
        // Additional ready state checks
        setTimeout(() => {
          const iframes = this.paymentElementTarget.querySelectorAll('iframe');
          console.log("🎉 [READY] Final iframe count:", iframes.length);
          console.log("🎉 [READY] Element final dimensions:", {
            width: this.paymentElementTarget.offsetWidth,
            height: this.paymentElementTarget.offsetHeight
          });
        }, 100);
      });

      this.paymentElement.on('change', (event) => {
        console.log("🔄 [CHANGE] Payment element changed:", {
          complete: event.complete,
          empty: event.empty,
          error: !!event.error
        });
        if (event.error) {
          console.warn("⚠️ [CHANGE] Payment element error:", event.error);
        }
      });

      this.paymentElement.on('focus', () => {
        console.log("👁️ [FOCUS] Payment element focused");
      });

      this.paymentElement.on('blur', () => {
        console.log("👁️ [BLUR] Payment element blurred");
      });

      // 3DS-specific event listeners
      this.paymentElement.on('loaderStart', () => {
        console.log("🔄 [3DS] Loader started - 3DS challenge may be starting");
      });

      this.paymentElement.on('loaderStop', () => {
        console.log("✅ [3DS] Loader stopped - 3DS challenge may have completed");
      });

      this.paymentElement.on('escape', () => {
        console.log("🚪 [3DS] Escape key pressed - user may be canceling 3DS");
      });

      // Listen for any 3DS-related changes
      this.paymentElement.on('change', (event) => {
        console.log("🔄 [3DS CHANGE] Payment element changed:", {
          complete: event.complete,
          empty: event.empty,
          error: !!event.error,
          value: event.value
        });
        
        // Check for 3DS-specific properties
        if (event.value && event.value.type === 'card') {
          console.log("💳 [3DS CARD] Card details:", {
            brand: event.value.card?.brand,
            last4: event.value.card?.last4,
            country: event.value.card?.country
          });
        }
        
        if (event.error) {
          console.warn("⚠️ [3DS ERROR] Payment element error:", event.error);
        }
      });
      
    } catch (e) {
      console.error("❌ [MOUNT] Stripe element mount error:", e);
      console.error("❌ [MOUNT] Mount error details:", e.message, e.stack);
      this.messageTarget.textContent = "Payment form failed to load. Please refresh the page.";
    }
    
    console.log("🏗️ [SETUP] Payment element setup completed");
  }

  setLoading(isLoading) {
    const element = this.submitOutletElement

    if (isLoading) {
      element.setAttribute("disabled", "")
    } else {
      element.removeAttribute("disabled")
    }
  }

  getPaymentMethodId() {
    // Extract payment method ID from the radio selector
    const radioElement = this.radioOutletElement;
    if (radioElement) {
      const match = radioElement.id.match(/payment_method_id_(\d+)/);
      return match ? match[1] : null;
    }
    return null;
  }

  async handle3DSChallenge(clientSecret) {
    try {
      // Confirm the Payment Intent with the client secret
      console.log("🔐 [3DS CHALLENGE] Calling stripe.confirmPayment...");
      
      // Use a simple return URL that redirects to the checkout confirm page
      const returnUrl = window.location.origin + '/checkout/confirm?payment_intent=' + clientSecret.split('_secret_')[0];
      

      
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
      });

      
      if (error) {

        this.messageTarget.textContent = error.message;
        this.setLoading(false);
        return;
      }

      
      // The payment will be finalized via webhook
      this.messageTarget.textContent = "Payment completed successfully!";
      
    } catch (error) {
      this.messageTarget.textContent = "3D Secure authentication failed. Please try again.";
      this.setLoading(false);
    }
  }
}

